import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSocket } from '../contexts/SocketContext';
import api from '../services/api';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import Logo from '../components/Logo';
import BurgerMenu from '../components/BurgerMenu';
import OfflineIndicator from '../components/OfflineIndicator';
import { WebSocketStatus } from '../components/WebSocketStatus';
import { ShoppingList } from '../types';

const CARD_COLORS = [
  { 
    name: 'Standard', 
    value: null, 
    light: { bg: 'var(--bg-primary)', border: 'var(--border-color)' },
    dark: { bg: 'var(--bg-secondary)', border: 'var(--border-color)' }
  },
  { 
    name: 'Blau', 
    value: 'blue', 
    light: { bg: '#EFF6FF', border: '#BFDBFE' },
    dark: { bg: '#1e3a5f', border: '#60a5fa' } // Hellerer Border für besseren Kontrast
  },
  { 
    name: 'Grün', 
    value: 'green', 
    light: { bg: '#F0FDF4', border: '#BBF7D0' },
    dark: { bg: '#1a3d2e', border: '#4ade80' } // Hellerer Border
  },
  { 
    name: 'Gelb', 
    value: 'yellow', 
    light: { bg: '#FEFCE8', border: '#FDE047' },
    dark: { bg: '#3d3516', border: '#facc15' } // Hellerer Border
  },
  { 
    name: 'Orange', 
    value: 'orange', 
    light: { bg: '#FFF7ED', border: '#FED7AA' },
    dark: { bg: '#3d2817', border: '#fb923c' }
  },
  { 
    name: 'Rot', 
    value: 'red', 
    light: { bg: '#FEF2F2', border: '#FECACA' },
    dark: { bg: '#3d1a1a', border: '#f87171' }
  },
  { 
    name: 'Lila', 
    value: 'purple', 
    light: { bg: '#FAF5FF', border: '#E9D5FF' },
    dark: { bg: '#2d1b3d', border: '#c084fc' } // Hellerer Border
  },
  { 
    name: 'Rosa', 
    value: 'pink', 
    light: { bg: '#FDF2F8', border: '#FBCFE8' },
    dark: { bg: '#3d1a2e', border: '#f9a8d4' } // Hellerer Border
  },
];

export default function Lists() {
  const navigate = useNavigate();
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListType, setNewListType] = useState<'SHOPPING' | 'TODO'>('SHOPPING');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editingListName, setEditingListName] = useState('');
  const [colorPickerListId, setColorPickerListId] = useState<string | null>(null);
  const [draggedListId, setDraggedListId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [menuOpenListId, setMenuOpenListId] = useState<string | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'ALL' | 'SHOPPING' | 'TODO'>('ALL');
  const [templates, setTemplates] = useState<{ id: string; name: string; listType: string; itemCount: number }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [saveTemplateDialog, setSaveTemplateDialog] = useState<{ listId: string; name: string } | null>(null);
  const { user, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const { socket } = useSocket();

  useEffect(() => {
    fetchLists();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handler = (data: { listId: string; type: 'item' | 'chat' }) => {
      setLists(prev => prev.map(l => {
        if (l.id !== data.listId) return l;
        if (data.type === 'chat') return { ...l, hasUnreadChat: true };
        if (data.type === 'item') return { ...l, unreadItemCount: (l.unreadItemCount ?? 0) + 1 };
        return l;
      }));
    };
    socket.on('list_badge_update', handler);
    return () => { socket.off('list_badge_update', handler); };
  }, [socket]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (colorPickerListId) {
        setColorPickerListId(null);
      }
      if (menuOpenListId) {
        setMenuOpenListId(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [colorPickerListId, menuOpenListId]);

  useEffect(() => {
    // Cleanup timer on unmount
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC zum Schließen von Dialogen
      if (e.key === 'Escape') {
        if (colorPickerListId) {
          setColorPickerListId(null);
          e.preventDefault();
        } else if (menuOpenListId) {
          setMenuOpenListId(null);
          e.preventDefault();
        } else if (editingListId) {
          cancelEditing();
          e.preventDefault();
        } else if (showNewListForm) {
          setShowNewListForm(false);
          setNewListName('');
          setNewListType('SHOPPING');
          e.preventDefault();
        }
      }
      
      // Enter zum Speichern beim Editieren
      if (e.key === 'Enter' && editingListId && !e.shiftKey) {
        const form = document.querySelector(`form[data-editing-id="${editingListId}"]`) as HTMLFormElement;
        if (form) {
          form.requestSubmit();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [colorPickerListId, menuOpenListId, editingListId, showNewListForm]);

  const fetchLists = async () => {
    try {
      const [listsRes, templatesRes] = await Promise.all([
        api.get('/lists'),
        api.get('/lists/templates'),
      ]);
      setLists(listsRes.data.data);
      setTemplates(templatesRes.data.data);
    } catch (error) {
      console.error('Error fetching lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAsTemplate = (listId: string, listName: string) => {
    setSaveTemplateDialog({ listId, name: listName });
  };

  const confirmSaveTemplate = async () => {
    if (!saveTemplateDialog) return;
    const { listId, name } = saveTemplateDialog;
    try {
      await api.post(`/lists/${listId}/save-as-template`, { name: name.trim() || undefined });
      setSaveTemplateDialog(null);
      setToast({ message: `"${name.trim()}" als Vorlage gespeichert`, type: 'success' });
      fetchLists();
    } catch (error) {
      console.error('Error saving template:', error);
      setToast({ message: 'Fehler beim Speichern der Vorlage', type: 'error' });
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      await api.delete(`/lists/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(null);
        setNewListName('');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setToast({ message: 'Fehler beim Löschen der Vorlage', type: 'error' });
    }
  };

  const createListFromTemplate = async (templateId: string, name: string) => {
    try {
      const response = await api.post(`/lists/from-template/${templateId}`, { name });
      const { id, name: createdName } = response.data.data;
      vibrate();
      setToast({ message: `"${createdName}" erstellt`, type: 'success' });
      setShowNewListForm(false);
      setSelectedTemplateId(null);
      setNewListName('');
      setNewListType('SHOPPING');
      await fetchLists();
      navigate(`/lists/${id}`);
    } catch (error) {
      console.error('Error creating from template:', error);
      setToast({ message: 'Fehler beim Erstellen aus Vorlage', type: 'error' });
    }
  };

  const createList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListName.trim()) return;

    if (selectedTemplateId) {
      await createListFromTemplate(selectedTemplateId, newListName.trim());
      return;
    }

    try {
      await api.post('/lists', { name: newListName, listType: newListType });
      const createdType = newListType;
      setNewListName('');
      setNewListType('SHOPPING');
      setShowNewListForm(false);
      vibrate(); // Haptic Feedback bei Erfolg
      setToast({ message: 'Liste erfolgreich erstellt', type: 'success' });
      // Wechsle zum entsprechenden Tab
      setActiveTab(createdType);
      fetchLists();
    } catch (error) {
      console.error('Error creating list:', error);
      setToast({ message: 'Fehler beim Erstellen der Liste', type: 'error' });
    }
  };

  const deleteList = async (listId: string, listName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setConfirmDialog({
      title: 'Liste löschen',
      message: `Möchtest du die Liste "${listName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.delete(`/lists/${listId}`);
          vibrate(); // Haptic Feedback bei Löschung
          setToast({ message: 'Liste erfolgreich gelöscht', type: 'success' });
          fetchLists();
        } catch (error) {
          console.error('Error deleting list:', error);
          setToast({ message: 'Fehler beim Löschen der Liste', type: 'error' });
        }
      },
    });
  };

  const leaveList = async (listId: string, listName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setConfirmDialog({
      title: 'Liste verlassen',
      message: `Möchtest du die Liste "${listName}" wirklich verlassen?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.post(`/lists/${listId}/leave`);
          setToast({ message: 'Liste erfolgreich verlassen', type: 'success' });
          fetchLists();
        } catch (error) {
          console.error('Error leaving list:', error);
          setToast({ message: 'Fehler beim Verlassen der Liste', type: 'error' });
        }
      },
    });
  };

  const startEditingList = (listId: string, currentName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingListId(listId);
    setEditingListName(currentName);
  };

  const cancelEditing = () => {
    setEditingListId(null);
    setEditingListName('');
  };

  const saveListName = async (listId: string, e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!editingListName.trim()) {
      setToast({ message: 'Listen-Name darf nicht leer sein', type: 'error' });
      return;
    }

    try {
      await api.patch(`/lists/${listId}`, { name: editingListName });
      vibrate(); // Haptic Feedback bei Umbenennung
      setToast({ message: 'Liste erfolgreich umbenannt', type: 'success' });
      setEditingListId(null);
      setEditingListName('');
      fetchLists();
    } catch (error) {
      console.error('Error renaming list:', error);
      setToast({ message: 'Fehler beim Umbenennen der Liste', type: 'error' });
    }
  };

  const toggleColorPicker = (listId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setColorPickerListId(colorPickerListId === listId ? null : listId);
    setMenuOpenListId(null);
  };

  const toggleMenu = (listId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpenListId(menuOpenListId === listId ? null : listId);
  };

  const setListColor = async (listId: string, color: string | null, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await api.patch(`/lists/${listId}/preferences`, { color });
      vibrate(); // Haptic Feedback bei Farbwahl
      setColorPickerListId(null);
      fetchLists();
    } catch (error) {
      console.error('Error setting list color:', error);
      setToast({ message: 'Fehler beim Setzen der Farbe', type: 'error' });
    }
  };

  const getCardColors = (colorValue: string | null | undefined) => {
    const color = CARD_COLORS.find(c => c.value === colorValue) || CARD_COLORS[0];
    return isDarkMode ? color.dark : color.light;
  };

  const handleDragStart = (listId: string, e: React.DragEvent) => {
    setDraggedListId(listId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (targetListId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedListId && draggedListId !== targetListId) {
      setDropTargetId(targetListId);
    }
  };

  const handleDrop = async (targetListId: string, e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedListId || draggedListId === targetListId) {
      setDraggedListId(null);
      return;
    }

    // Arbeite mit allen Listen für globale Sortierung
    const draggedIndex = lists.findIndex(l => l.id === draggedListId);
    const targetIndex = lists.findIndex(l => l.id === targetListId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedListId(null);
      return;
    }

    // Optimistic UI update auf allen Listen
    const newLists = [...lists];
    const [draggedItem] = newLists.splice(draggedIndex, 1);
    newLists.splice(targetIndex, 0, draggedItem);
    setLists(newLists);
    setDraggedListId(null);
    setDropTargetId(null);

    // Send new order to backend
    try {
      const listOrder = newLists.map((list, index) => ({
        listId: list.id,
        sortOrder: index
      }));
      
      await api.post('/lists/reorder', { listOrder });
    } catch (error) {
      console.error('Error reordering lists:', error);
      setToast({ message: 'Fehler beim Sortieren der Listen', type: 'error' });
      // Revert on error
      fetchLists();
    }
  };

  const handleDragEnd = () => {
    setDraggedListId(null);
    setDropTargetId(null);
  };

  const vibrate = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50); // Kurze 50ms Vibration
    }
  };

  const handleTouchStart = (listId: string, e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    
    // Long press für Drag-Modus (500ms)
    const timer = setTimeout(() => {
      setDraggedListId(listId);
      vibrate(); // Vibration als Feedback
    }, 500);
    
    setLongPressTimer(timer);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    // Wenn im Drag-Modus, verhindere Scrollen
    if (draggedListId) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (targetListId: string) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    // Wenn wir im Drag-Modus sind und es eine andere Liste ist
    if (draggedListId && draggedListId !== targetListId) {
      // Arbeite mit allen Listen für globale Sortierung
      const draggedIndex = lists.findIndex(l => l.id === draggedListId);
      const targetIndex = lists.findIndex(l => l.id === targetListId);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Optimistic UI update auf allen Listen
        const newLists = [...lists];
        const [draggedItem] = newLists.splice(draggedIndex, 1);
        newLists.splice(targetIndex, 0, draggedItem);
        setLists(newLists);
        
        vibrate(); // Vibration beim Drop

        // Send new order to backend
        api.post('/lists/reorder', { 
          listOrder: newLists.map((list, index) => ({
            listId: list.id,
            sortOrder: index
          }))
        }).catch(error => {
          console.error('Error reordering lists:', error);
          setToast({ message: 'Fehler beim Sortieren der Listen', type: 'error' });
          fetchLists();
        });
      }
    }
    
    setDraggedListId(null);
    setDropTargetId(null);
    setTouchStartY(null);
  };

  // Filtere Listen basierend auf aktivem Tab
  const filteredLists = lists.filter(list => {
    if (activeTab === 'ALL') return true;
    return list.listType === activeTab;
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)' }}>
      <header style={{ 
        backgroundColor: 'var(--bg-primary)',
        padding: '1.5rem',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Logo variant="dark" />
          <BurgerMenu currentPage="lists" />
        </div>
      </header>

      <main className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          {!showNewListForm ? (
            <button
              onClick={() => setShowNewListForm(true)}
              className="btn btn-primary"
              aria-label="Neue Liste erstellen"
              style={{ width: '100%' }}
            >
              + Neue Liste erstellen
            </button>
          ) : (
            <form onSubmit={createList} className="card">
              {/* List Type Selection */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  marginBottom: '0.5rem',
                  color: 'var(--text-primary)'
                }}>
                  Listen-Typ
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setNewListType('SHOPPING')}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      border: newListType === 'SHOPPING' ? '2px solid var(--primary)' : '2px solid var(--border-color)',
                      borderRadius: '0.5rem',
                      background: newListType === 'SHOPPING' ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '2rem' }}>🛒</span>
                    <span style={{ 
                      fontWeight: '600', 
                      color: newListType === 'SHOPPING' ? 'var(--primary)' : 'var(--text-primary)'
                    }}>
                      Einkaufsliste
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewListType('TODO')}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      border: newListType === 'TODO' ? '2px solid var(--primary)' : '2px solid var(--border-color)',
                      borderRadius: '0.5rem',
                      background: newListType === 'TODO' ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '2rem' }}>✓</span>
                    <span style={{ 
                      fontWeight: '600', 
                      color: newListType === 'TODO' ? 'var(--primary)' : 'var(--text-primary)'
                    }}>
                      Aufgabenliste
                    </span>
                  </button>
                </div>
              </div>

              {templates.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                    Aus Vorlage starten
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {templates.map(t => (
                      <div
                        key={t.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          border: `1px solid ${selectedTemplateId === t.id ? 'var(--primary)' : 'var(--border-color)'}`,
                          borderRadius: '0.5rem',
                          overflow: 'hidden',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTemplateId(t.id);
                            setNewListName(t.name);
                            setNewListType(t.listType as 'SHOPPING' | 'TODO');
                          }}
                          style={{
                            flex: 1,
                            padding: '0.625rem 0.875rem',
                            border: 'none',
                            background: selectedTemplateId === t.id ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '0.875rem',
                            color: 'var(--text-primary)'
                          }}
                          onMouseEnter={(e) => { if (selectedTemplateId !== t.id) e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'; }}
                          onMouseLeave={(e) => { if (selectedTemplateId !== t.id) e.currentTarget.style.backgroundColor = 'var(--bg-primary)'; }}
                        >
                          <span>{t.listType === 'TODO' ? '✅' : '🛒'} {t.name}</span>
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>{t.itemCount} Einträge</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTemplate(t.id)}
                          title="Vorlage löschen"
                          style={{
                            padding: '0.5rem 0.75rem',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.75rem 0 0' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>oder neu erstellen</span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                  </div>
                </div>
              )}
              
              <input
                type="text"
                className="input"
                placeholder="Listen-Name"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                autoFocus
                style={{ marginBottom: '1rem' }}
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Erstellen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewListForm(false);
                    setNewListName('');
                    setNewListType('SHOPPING');
                    setSelectedTemplateId(null);
                  }}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Tab Navigation - kompakt & unscheinbar */}
        <div style={{ 
          display: 'flex', 
          gap: '0.375rem',
          marginBottom: '1.5rem',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => {
              setActiveTab('ALL');
              vibrate();
            }}
            aria-label="Alle Listen anzeigen"
            aria-pressed={activeTab === 'ALL'}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: activeTab === 'ALL' ? 'var(--bg-primary)' : 'transparent',
              color: activeTab === 'ALL' ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'ALL' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.875rem',
              minHeight: '36px',
              borderBottom: activeTab === 'ALL' ? '2px solid var(--primary)' : '2px solid transparent'
            }}
          >
            Alle ({lists.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('SHOPPING');
              vibrate();
            }}
            aria-label="Nur Einkaufslisten anzeigen"
            aria-pressed={activeTab === 'SHOPPING'}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: activeTab === 'SHOPPING' ? 'var(--bg-primary)' : 'transparent',
              color: activeTab === 'SHOPPING' ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'SHOPPING' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.875rem',
              minHeight: '36px',
              borderBottom: activeTab === 'SHOPPING' ? '2px solid var(--primary)' : '2px solid transparent'
            }}
          >
            🛒 Einkauf ({lists.filter(l => l.listType === 'SHOPPING').length})
          </button>
          <button
            onClick={() => {
              setActiveTab('TODO');
              vibrate();
            }}
            aria-label="Nur Aufgabenlisten anzeigen"
            aria-pressed={activeTab === 'TODO'}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: activeTab === 'TODO' ? 'var(--bg-primary)' : 'transparent',
              color: activeTab === 'TODO' ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'TODO' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.875rem',
              minHeight: '36px',
              borderBottom: activeTab === 'TODO' ? '2px solid var(--primary)' : '2px solid transparent'
            }}
          >
            ✓ Aufgaben ({lists.filter(l => l.listType === 'TODO').length})
          </button>
        </div>

        {filteredLists.length === 0 && lists.length > 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</p>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Keine {activeTab === 'SHOPPING' ? 'Einkaufslisten' : 'Aufgabenlisten'}
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              Du hast noch keine {activeTab === 'SHOPPING' ? 'Einkaufslisten' : 'Aufgabenlisten'} erstellt
            </p>
          </div>
        ) : lists.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</p>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Keine Listen vorhanden
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              Erstelle deine erste Liste
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {filteredLists.map((list) => {
              const progress = (list.itemCount ?? 0) > 0 ? ((list.checkedCount ?? 0) / (list.itemCount ?? 1)) * 100 : 0;
              const isOwner = list.ownerId === user?.id;
              const cardColors = getCardColors(list.color);
              return (
                <div
                  key={list.id}
                  className="list-card"
                  style={{ 
                    position: 'relative',
                    zIndex: menuOpenListId === list.id || colorPickerListId === list.id ? 100 : 1
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(list.id, e)}
                  onDragOver={(e) => handleDragOver(list.id, e)}
                  onDrop={(e) => handleDrop(list.id, e)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(list.id, e)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={() => handleTouchEnd(list.id)}
                >
                  <Link
                    to={`/lists/${list.id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="card" style={{ 
                      cursor: draggedListId ? 'grabbing' : 'grab', 
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      backgroundColor: cardColors.bg,
                      borderColor: cardColors.border,
                      opacity: draggedListId === list.id ? 0.6 : 1,
                      transform: draggedListId === list.id 
                        ? 'scale(0.95) rotate(-2deg)' // Leichte Rotation beim Greifen
                        : dropTargetId === list.id 
                        ? 'scale(1.02)' // Vergrößerung beim Hover
                        : 'scale(1)',
                      boxShadow: draggedListId === list.id 
                        ? '0 8px 24px rgba(0, 0, 0, 0.3)' 
                        : dropTargetId === list.id
                        ? '0 4px 16px rgba(var(--primary-rgb, 59, 130, 246), 0.3)'
                        : undefined,
                      // Besserer Drop-Indikator
                      ...(dropTargetId === list.id && {
                        border: '2px solid var(--primary)',
                        backgroundColor: isDarkMode 
                          ? 'rgba(59, 130, 246, 0.1)' 
                          : 'rgba(59, 130, 246, 0.05)'
                      })
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                          <span 
                            style={{ 
                              cursor: 'grab', 
                              color: 'var(--text-secondary)', 
                              fontSize: '1.5rem',
                              lineHeight: 1,
                              userSelect: 'none',
                              padding: '0.25rem'
                            }}
                            title="Liste verschieben"
                          >
                            ⋮⋮
                          </span>
                          {editingListId === list.id ? (
                          <form 
                            onSubmit={(e) => saveListName(list.id, e)}
                            onClick={(e) => e.stopPropagation()}
                            data-editing-id={list.id}
                            style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'center' }}
                          >
                            <input
                              type="text"
                              className="input"
                              value={editingListName}
                              onChange={(e) => setEditingListName(e.target.value)}
                              autoFocus
                              aria-label="Listen-Name bearbeiten"
                              style={{ flex: 1, fontSize: '1.125rem', padding: '0.5rem' }}
                              onBlur={() => {
                                setTimeout(cancelEditing, 200);
                              }}
                            />
                            <button
                              type="submit"
                              aria-label="Speichern"
                              style={{
                                background: 'var(--success)',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                padding: '0.5rem 1rem',
                                borderRadius: '0.375rem',
                                minWidth: '44px',
                                minHeight: '44px'
                              }}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelEditing();
                              }}
                              aria-label="Abbrechen"
                              style={{
                                background: 'var(--gray-200)',
                                border: 'none',
                                color: 'var(--gray-700)',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                padding: '0.5rem 1rem',
                                borderRadius: '0.375rem',
                                minWidth: '44px',
                                minHeight: '44px'
                              }}
                            >
                              ✕
                            </button>
                          </form>
                        ) : (
                          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                            {list.listType === 'TODO' ? '✓' : '🛒'} {list.name}
                            {list.hasUnreadChat && (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--primary)',
                                flexShrink: 0,
                              }} title="Neue Chat-Nachrichten" />
                            )}
                            {!!list.unreadItemCount && (
                              <span style={{
                                background: 'var(--primary)',
                                color: '#0d1117',
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                borderRadius: '9999px',
                                padding: '0 5px',
                                lineHeight: '16px',
                                height: '16px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                flexShrink: 0,
                              }} title={`${list.unreadItemCount} neue Einträge`}>
                                {list.unreadItemCount} neu
                              </span>
                            )}
                          </h3>
                        )}
                        </div>
                        
                        {/* 3-Punkte-Menü für alle Aktionen */}
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={(e) => toggleMenu(list.id, e)}
                            aria-label="Listen-Optionen öffnen"
                            aria-expanded={menuOpenListId === list.id}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--gray-500)',
                              cursor: 'pointer',
                              fontSize: '1.5rem',
                              padding: '0.5rem',
                              borderRadius: '0.375rem',
                              transition: 'background-color 0.2s',
                              minWidth: '44px',
                              minHeight: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            ⋮
                          </button>
                          
                          {menuOpenListId === list.id && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '0.25rem',
                                backgroundColor: 'var(--bg-primary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '0.5rem',
                                boxShadow: 'var(--shadow-lg)',
                                zIndex: 1000,
                                minWidth: '200px',
                                overflow: 'hidden'
                              }}
                            >
                              <button
                                onClick={(e) => {
                                  startEditingList(list.id, list.name, e);
                                  setMenuOpenListId(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.875rem 1rem',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  transition: 'background-color 0.2s',
                                  minHeight: '48px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontSize: '1.25rem' }}>✏️</span>
                                <span>Umbenennen</span>
                              </button>
                              
                              <button
                                onClick={(e) => {
                                  toggleColorPicker(list.id, e);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.875rem 1rem',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  transition: 'background-color 0.2s',
                                  minHeight: '48px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontSize: '1.25rem' }}>🎨</span>
                                <span>Farbe ändern</span>
                              </button>
                              
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const newType = list.listType === 'SHOPPING' ? 'TODO' : 'SHOPPING';
                                  try {
                                    await api.patch(`/lists/${list.id}`, { listType: newType });
                                    setToast({ 
                                      message: `Liste ist jetzt eine ${newType === 'SHOPPING' ? 'Einkaufsliste' : 'Aufgabenliste'}`, 
                                      type: 'success' 
                                    });
                                    fetchLists();
                                  } catch (error) {
                                    console.error('Error changing list type:', error);
                                    setToast({ message: 'Fehler beim Ändern des Listen-Typs', type: 'error' });
                                  }
                                  setMenuOpenListId(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.875rem 1rem',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  transition: 'background-color 0.2s',
                                  minHeight: '48px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontSize: '1.25rem' }}>{list.listType === 'SHOPPING' ? '✓' : '🛒'}</span>
                                <span>Als {list.listType === 'SHOPPING' ? 'Aufgabenliste' : 'Einkaufsliste'}</span>
                              </button>
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setMenuOpenListId(null);
                                  saveAsTemplate(list.id, list.name);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.875rem 1rem',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  transition: 'background-color 0.2s',
                                  minHeight: '48px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontSize: '1.25rem' }}>📋</span>
                                <span>Als Vorlage speichern</span>
                              </button>

                              <div style={{ height: '1px', backgroundColor: 'var(--border-color)', margin: '0.25rem 0' }} />
                              
                              {isOwner ? (
                                <button
                                  onClick={(e) => {
                                    deleteList(list.id, list.name, e);
                                    setMenuOpenListId(null);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.875rem 1rem',
                                    border: 'none',
                                    background: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    color: 'var(--danger)',
                                    transition: 'background-color 0.2s',
                                    minHeight: '48px'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <span style={{ fontSize: '1.25rem' }}>🗑️</span>
                                  <span>Löschen</span>
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    leaveList(list.id, list.name, e);
                                    setMenuOpenListId(null);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.875rem 1rem',
                                    border: 'none',
                                    background: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    color: 'var(--danger)',
                                    transition: 'background-color 0.2s',
                                    minHeight: '48px'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <span style={{ fontSize: '1.25rem' }}>🚪</span>
                                  <span>Liste verlassen</span>
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Color Picker - jetzt außerhalb und besser positioniert */}
                          {colorPickerListId === list.id && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '0.25rem',
                                backgroundColor: 'var(--bg-primary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '0.5rem',
                                padding: '1rem',
                                boxShadow: 'var(--shadow-lg)',
                                zIndex: 1000,
                                minWidth: '280px'
                              }}
                            >
                              <div style={{ marginBottom: '0.75rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                Farbe wählen
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                                gap: '0.75rem'
                              }}>
                                {CARD_COLORS.map(color => (
                                  <button
                                    key={color.value || 'default'}
                                    onClick={(e) => setListColor(list.id, color.value, e)}
                                    style={{
                                      width: '56px',
                                      height: '56px',
                                      borderRadius: '0.5rem',
                                      border: `2px solid ${isDarkMode ? color.dark.border : color.light.border}`,
                                      backgroundColor: isDarkMode ? color.dark.bg : color.light.bg,
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s',
                                      outline: list.color === color.value ? '3px solid var(--primary)' : 'none',
                                      outlineOffset: '2px',
                                      position: 'relative'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    title={color.name}
                                  >
                                    {list.color === color.value && (
                                      <span style={{ 
                                        position: 'absolute', 
                                        top: '50%', 
                                        left: '50%', 
                                        transform: 'translate(-50%, -50%)', 
                                        fontSize: '1.5rem',
                                        color: 'var(--text-primary)',
                                        textShadow: '0 0 2px rgba(0,0,0,0.5)'
                                      }}>
                                        ✓
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {list.description && (
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                          {list.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                        <span>📦 {list.itemCount} Artikel</span>
                        <span>✓ {list.checkedCount}/{list.itemCount} erledigt</span>
                      </div>
                      {(list.itemCount ?? 0) > 0 && (
                        <div style={{ height: '4px', backgroundColor: 'var(--gray-200)', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ 
                            height: '100%', 
                            width: `${progress}%`, 
                            backgroundColor: progress === 100 ? 'var(--success)' : 'var(--primary)',
                            transition: 'width 0.3s ease',
                            borderRadius: '2px'
                          }} />
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <OfflineIndicator />
      <WebSocketStatus />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {saveTemplateDialog && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setSaveTemplateDialog(null); }}
        >
          <div
            style={{
              background: 'var(--bg-secondary)', borderRadius: '1rem',
              padding: '1.5rem', width: '100%', maxWidth: '400px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.125rem', color: 'var(--text-primary)' }}>Vorlage benennen</h3>
            <input
              type="text"
              className="input"
              value={saveTemplateDialog.name}
              onChange={(e) => setSaveTemplateDialog(prev => prev ? { ...prev, name: e.target.value } : null)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmSaveTemplate(); if (e.key === 'Escape') setSaveTemplateDialog(null); }}
              autoFocus
              style={{ marginBottom: '1rem' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={confirmSaveTemplate}>
                Speichern
              </button>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setSaveTemplateDialog(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
